import { EnterpriseBadge, Select, Typography, useToast } from "@humansignal/ui";
import React from "react";
import { useHistory } from "react-router";
import { ToggleItems } from "../../components";
import { Button } from "@humansignal/ui";
import { Modal } from "../../components/Modal/Modal";
import { Space } from "../../components/Space/Space";
import { useAPI } from "../../providers/ApiProvider";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { cn } from "../../utils/bem";
import { ConfigPage } from "./Config/Config";
import "./CreateProject.prefix.css";
import { ImportPage as OwnImportPage } from "./Import/Import";
import { importStep as extensionImportStep, wizardPanels } from "@afriannotate/extensions";
import { useImportPage } from "./Import/useImportPage";
import { requiredKeysFromConfig } from "./utils/labelConfig";
import { useDraftProject } from "./utils/useDraftProject";
import { Input, TextArea } from "../../components/Form";
import { useTranslation } from "../../i18n/LocaleProvider";
// An installed extension may bring its own Import step (same props).
const ImportPage = extensionImportStep?.ImportPage ?? OwnImportPage;

const ProjectName = ({
  name,
  setName,
  onSaveName,
  onSubmit,
  error,
  description,
  setDescription,
  workspaces,
  workspaceId,
  setWorkspaceId,
  plan,
  setPlan,
  panels,
  fields = true,
  show = true,
}) => {
  const { t } = useTranslation();
  const field = {
    height: 40,
    boxSizing: "border-box",
    padding: "0 12px",
    border: "1px solid var(--color-neutral-border)",
    borderRadius: 5,
    background: "var(--color-neutral-background)",
    color: "var(--color-neutral-content)",
    fontSize: "var(--font-size-body-small)",
    width: "100%",
  };
  return !show ? null : (
    <form
      className={cn("project-name").toClassName()}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {/* An installed extension may offer a head start here (a draft from
          a description). It fills the fields below; nothing is created. */}
      {panels}
      {!fields ? null : (
        <>
          <div className="w-full flex flex-col gap-2">
            <label className="w-full" htmlFor="project_name">
              {t("pages.createproject.create_project.project_name")}
            </label>
            <Input
              name="name"
              id="project_name"
              value={name ?? ""}
              onChange={(e) => setName(e.target.value ?? "")}
              onBlur={onSaveName}
              placeholder={t("pages.createproject.create_project.eg_hausa_sentiment_v1")}
              required
              className="project-title w-full"
            />
            {error && <span className="-mt-1 text-negative-content">{error}</span>}
          </div>
          <div className="w-full flex flex-col gap-2">
            <label className="w-full" htmlFor="project_description">
              {t("pages.createproject.create_project.description")}
            </label>
            <TextArea
              name="description"
              id="project_description"
              placeholder={t("pages.createproject.create_project.optional_description_of_your_project")}
              rows="4"
              style={{ minHeight: 100 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="project-description w-full"
            />
          </div>
          {/* Workspace picker. ``null`` / empty means "unassigned" — the
          project lives at the org root. The select is live (not the
          Enterprise-gated placeholder that used to sit here). */}
          <div className="w-full flex flex-col gap-2">
            <label className="w-full" htmlFor="project_workspace">
              {t("pages.createproject.create_project.workspace")}
            </label>
            <select
              id="project_workspace"
              value={workspaceId ?? ""}
              onChange={(e) => setWorkspaceId(e.target.value || null)}
              style={{
                padding: "8px 10px",
                border: "1px solid var(--color-neutral-border)",
                borderRadius: 6,
                background: "var(--color-neutral-background)",
                color: "var(--color-neutral-content)",
                fontSize: "var(--font-size-body-small)",
              }}
            >
              <option value="">{t("pages.createproject.create_project.unassigned_org_root")}</option>
              {(workspaces ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.title}
                </option>
              ))}
            </select>
            <Typography size="small" className="mt-tight mb-wider">
              {t("pages.createproject.create_project.workspaces_help")}
            </Typography>
          </div>
          {/* The plan (owner-requested, tracker item 18): what the data is
          for, how many tasks it should reach, in which languages, by
          when. All optional; the data card opens with it and the
          Analytics page forecasts against the target. */}
          <div className="w-full flex flex-col gap-2">
            <label className="w-full" htmlFor="project_goal">
              Goal <span style={{ color: "var(--color-neutral-content-subtler)" }} />
            </label>
            <Input
              id="project_goal"
              value={plan?.goal ?? ""}
              onChange={(e) => setPlan({ ...plan, goal: e.target.value })}
              placeholder="e.g. A sentiment corpus of Hausa news headlines for a classifier"
              className="w-full"
            />
          </div>
          <div className="w-full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div className="flex flex-col gap-2">
              <label htmlFor="project_target">Target tasks</label>
              <input
                id="project_target"
                type="number"
                min="0"
                value={plan?.target_tasks ?? ""}
                onChange={(e) => setPlan({ ...plan, target_tasks: e.target.value })}
                placeholder="e.g. 5000"
                style={field}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="project_languages">Languages</label>
              <input
                id="project_languages"
                value={plan?.languages ?? ""}
                onChange={(e) => setPlan({ ...plan, languages: e.target.value })}
                placeholder="Hausa, English"
                style={field}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="project_deadline">Planned by</label>
              <input
                id="project_deadline"
                type="date"
                value={plan?.deadline ?? ""}
                onChange={(e) => setPlan({ ...plan, deadline: e.target.value })}
                style={field}
              />
            </div>
          </div>
          {/* Retention disclosure — the user must know at creation time that
          a retention policy may auto-delete this project's DATA. Phrased
          conditionally so it doesn't alarm when no policy is set. */}
          <Typography size="small" className="mt-tight" style={{ color: "var(--color-neutral-content-subtler)" }}>
            Data retention: if your organization sets a retention policy, this project's data (tasks, annotations, and
            uploads — not the project itself) is automatically deleted after that period. You'll be warned by email and
            in-app before then.
          </Typography>
        </>
      )}
    </form>
  );
};

export const CreateProject = ({ onClose, initialTemplate, initialName, initialImport }) => {
  const { t } = useTranslation();
  const toast = useToast();
  // If an org template was pre-selected, skip straight to the Config step.
  const [step, _setStep] = React.useState(initialTemplate ? "config" : "name");
  const [waiting, setWaitingStatus] = React.useState(false);

  const { project, setProject: updateProject, markSaved } = useDraftProject();
  const history = useHistory();
  const api = useAPI();
  // Title reflects the active step so the tab reads "Configure | New
  // Project | …" / "Import | New Project | …" while the wizard is
  // open. Mirrors the section titles the user sees inside the modal.
  useUpdatePageTitle(
    createTitleFromSegments([step === "config" ? "Configure" : step === "import" ? "Import" : "Name", "New Project"]),
  );

  const [name, setName] = React.useState(initialName ?? (initialTemplate?.name ? `${initialTemplate.name}` : ""));
  const [error, setError] = React.useState();
  const [description, setDescription] = React.useState(initialTemplate?.description ?? "");
  const [plan, setPlan] = React.useState({ goal: "", target_tasks: "", languages: "", deadline: "" });
  const [sample, setSample] = React.useState(null);
  // A labeling setup filled in by a wizard panel; the Config step opens
  // on it instead of the template list.
  const [draftConfig, setDraftConfig] = React.useState("");
  // A panel marked ``covers`` stands in for this step's own boxes: the
  // person writes one description instead of filling a form. The boxes
  // come back the moment there is something to look over, or when the
  // panel says it has nothing to offer (no model connected, say).
  const covering = React.useMemo(() => (wizardPanels ?? []).some((panel) => panel.covers), []);
  const [fieldsShown, setFieldsShown] = React.useState(!covering);
  const [workspaceId, setWorkspaceId] = React.useState(null);
  const [workspaces, setWorkspaces] = React.useState([]);

  // Load the current org's workspaces once for the picker. Scoped to the
  // active org by the backend; new projects don't yet have an org set so
  // we pass no ``org`` filter — the endpoint already applies the viewer's
  // active org.
  React.useEffect(() => {
    api.callApi("workspaces", { suppressError: true }).then((res) => {
      if (Array.isArray(res)) setWorkspaces(res);
      else if (res?.results) setWorkspaces(res.results);
    });
  }, [api]);

  // When a saved template is handed in, pre-fill the draft project with its
  // label_config + settings as soon as the draft id is available. The
  // Config step's XML editor will then show the template's config.
  //
  // Critically: ``updateProject`` is also called on the LOCAL state so
  // ``labelConfigTrivial`` re-evaluates against the template's config
  // instead of the empty default. Without this, the Data Import tab
  // stays disabled (its ``disabled: labelConfigTrivial`` gate keeps
  // firing) even though the server-side config is non-trivial — the
  // gate reads from the in-memory project, not the API.
  React.useEffect(() => {
    if (!initialTemplate || !project?.id) return;
    const templatePayload = {
      label_config: initialTemplate.label_config,
      expert_instruction: initialTemplate.expert_instruction,
      show_instruction: initialTemplate.show_instruction,
      show_skip_button: initialTemplate.show_skip_button,
      enable_empty_annotation: initialTemplate.enable_empty_annotation,
      maximum_annotations: initialTemplate.maximum_annotations,
      sampling: initialTemplate.sampling,
      regions_min_per_annotation: initialTemplate.regions_min_per_annotation,
      regions_max_per_annotation: initialTemplate.regions_max_per_annotation,
    };
    updateProject({ ...project, ...templatePayload });
    api.callApi("updateProject", {
      params: { pk: project.id },
      body: templatePayload,
      suppressError: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, initialTemplate?.id]);

  // ``labelConfigTrivial`` is computed below from the in-memory project
  // state; we read its current value via a ref so the setStep callback
  // doesn't need to be recreated on every keystroke. The check
  // intentionally uses ref-on-call so newly-typed XML in the Config
  // step unblocks Import advancement immediately.
  const labelConfigTrivialRef = React.useRef(true);
  const setStep = React.useCallback((step) => {
    if (step === "import" && labelConfigTrivialRef.current) {
      // Silently keep the user on Labeling Setup. The tab itself
      // renders disabled (greyed out) so this branch is mostly a
      // safety net for keyboard-driven advancement.
      _setStep("config");
      return;
    }
    _setStep(step);
    const eventNameMap = {
      name: "project_name",
      import: "data_import",
      config: "labeling_setup",
    };
    __lsa(`create_project.tab.${eventNameMap[step]}`);
  }, []);

  React.useEffect(() => {
    setError(null);
  }, [name]);

  const { columns, uploading, uploadDisabled, finishUpload, finishSources, pageProps, uploadSample, blocked } =
    useImportPage(project, sample, initialImport);

  // Save-button gate: when the user HAS uploaded data AND the label
  // config expects $varname keys that the data doesn't include, refuse
  // to finalise. Without this gate the wizard would happily save and
  // the project would land on the dashboard with tasks that LSF can't
  // render. ``uploadedColumns`` is the unmasked column list from the
  // upload endpoint (different from ``columns`` which is masked to a
  // single placeholder while the user picks a CSV-handling mode).
  // Safe to leave Save enabled when:
  //   - no data uploaded yet (column list is empty) — user might be
  //     creating a project to populate later via the dashboard
  //   - CSV handling is still pending — column meaning is undecided
  //   - all required keys are present in the uploaded data
  const uploadedColumns = pageProps?.uploadedColumns ?? [];
  const csvPlaceholderActive = ["choose", "ts"].includes(pageProps?.csvHandling);
  const requiredKeysList = React.useMemo(() => requiredKeysFromConfig(project?.label_config), [project?.label_config]);
  // Column-mapping targets count as present: a column the user mapped to
  // $text satisfies $text even though the RAW column list doesn't include
  // it — without this, Save stayed disabled after a correct mapping.
  const mappingTargets = React.useMemo(() => {
    const targets = new Set();
    Object.values(pageProps?.csvMappings ?? {}).forEach((m) => {
      Object.values(m?.columns ?? {}).forEach((v) => {
        if (v && v !== "__skip__") targets.add(v);
      });
    });
    return targets;
  }, [pageProps?.csvMappings]);
  // A dropped media file carries no column names: the server reports the
  // single sentinel column ``$undefined$`` and binds the file to the
  // config's one object tag when the task is built. Comparing that against
  // the config's $video finds it "missing" and leaves Save disabled after a
  // perfectly good video upload. The backend discards the same sentinel
  // before its equivalent check.
  const singleUnnamedUpload = uploadedColumns.includes("$undefined$") && requiredKeysList.length <= 1;
  const dataMissingRequiredKeys =
    uploadedColumns.length > 0 &&
    !csvPlaceholderActive &&
    !singleUnnamedUpload &&
    requiredKeysList.some((k) => !uploadedColumns.includes(k) && !mappingTargets.has(k));

  const rootClass = cn("create-project");
  const tabClass = rootClass.elem("tab");

  // Detect whether the labeling config is "trivial" (the auto-generated
  // empty ``<View></View>``). If it is, gate Data Import behind it: the
  // user must pick or design a template before importing tasks. This
  // is a strict UX choice — without a config, the import has no way
  // to know which $varname keys it should require, and the validation
  // error users hit was unhelpful.
  const labelConfigTrivial = React.useMemo(() => {
    const cfg = (project?.label_config || "").replace(/\s+/g, "");
    return !cfg || cfg === "<View></View>";
  }, [project?.label_config]);

  // Sync the ref so the setStep guard sees the live value. Without
  // this, the ref stays stuck at its initial ``true`` and every click
  // on the Import tab silently bounces back to Config — even after
  // the user has picked a non-trivial template.
  React.useEffect(() => {
    labelConfigTrivialRef.current = labelConfigTrivial;
  }, [labelConfigTrivial]);

  // Tab order: Name → Labeling Setup → Data Import. Labeling sits in
  // slot 2 because the user explicitly asked for it: "users have to
  // select or design template before they are allowed to upload data".
  const steps = {
    name: (
      <span className={tabClass.mod({ disabled: !!error }).toClassName()}>
        {t("pages.createproject.create_project.project_name")}
      </span>
    ),
    config: (
      <span className={tabClass.mod({ disabled: false }).toClassName()}>
        {t("pages.createproject.create_project.labeling_setup")}
      </span>
    ),
    import: (
      <span className={tabClass.mod({ disabled: uploadDisabled || labelConfigTrivial }).toClassName()}>
        {t("pages.createproject.create_project.data_import")}
      </span>
    ),
  };

  // name intentionally skipped from deps:
  // this should trigger only once when we got project loaded
  React.useEffect(() => {
    project && !name && setName(project.title);
  }, [project]);

  const projectBody = React.useMemo(
    () => ({
      title: name,
      description,
      label_config: project?.label_config ?? "<View></View>",
      // ``null`` means "unassigned". The backend field is nullable, so
      // we always send it (omitting would leave the prior value).
      workspace: workspaceId || null,
      plan,
    }),
    [name, description, project?.label_config, workspaceId, plan],
  );

  const onCreate = React.useCallback(async () => {
    if (!(name ?? "").trim()) {
      setError("Please give the project a name before saving.");
      return;
    }
    // First, persist project with label_config so import/reimport validates against it
    const response = await api.callApi("updateProject", {
      params: {
        pk: project.id,
      },
      body: { ...projectBody, is_draft: false },
    });

    if (response === null) return;

    // From this point the project is a real, saved project — don't let the
    // wizard-unmount cleanup delete it.
    markSaved?.();

    const imported = await finishUpload();

    if (!imported) return;

    // Data chosen in an extension's Import tab is brought in now.
    const failed = await finishSources();
    if (failed) {
      toast?.show({ message: failed, type: "error" });
      return;
    }

    setWaitingStatus(true);

    if (sample) await uploadSample(sample);

    __lsa("create_project.create", { sample: sample?.url });

    setWaitingStatus(false);

    history.push(`/projects/${response.id}/data`);
  }, [project, projectBody, finishUpload, finishSources, name, markSaved]);

  // A wizard panel hands back a draft: the fields it filled in. The name,
  // description and plan are this form's own state; the labeling setup and
  // the instructions belong to the draft project, so they are saved on it.
  const applyDraft = React.useCallback(
    async (draft) => {
      if (!draft) return;
      if (draft.title) setName(draft.title);
      if (draft.description) setDescription(draft.description);
      if (draft.plan) setPlan((current) => ({ ...current, ...draft.plan }));
      const body = {};
      if (draft.label_config) body.label_config = draft.label_config;
      if (draft.instructions) {
        body.expert_instruction = draft.instructions;
        body.show_instruction = true;
      }
      if (draft.label_config) setDraftConfig(draft.label_config);
      setFieldsShown(true);
      if (!project?.id || !Object.keys(body).length) return;
      updateProject({ ...project, ...body });
      await api.callApi("updateProject", { params: { pk: project.id }, body, suppressError: true });
    },
    [project, api, updateProject],
  );

  const panels = React.useMemo(
    () =>
      (wizardPanels ?? []).map(({ key, component: Panel }) => (
        <Panel key={key} project={project} apply={applyDraft} reveal={() => setFieldsShown(true)} />
      )),
    [project, applyDraft],
  );

  const onSaveName = async () => {
    if (error) return;
    const res = await api.callApi("updateProjectRaw", {
      params: {
        pk: project.id,
      },
      body: {
        title: name,
      },
    });

    if (res.ok) return;
    const err = await res.json();

    setError(err.validation_errors?.title);
  };

  const onDelete = React.useCallback(() => {
    const performClose = async () => {
      setWaitingStatus(true);
      if (project)
        await api.callApi("deleteProject", {
          params: {
            pk: project.id,
          },
          suppressError: true,
        });
      setWaitingStatus(false);
      // Tell the draft hook we've already handled the teardown so its
      // unmount cleanup doesn't fire a second DELETE against the now-gone
      // project id (which would 404 in devtools network tab).
      markSaved?.();
      updateProject(null);
      onClose?.();
    };
    performClose();
  }, [project, markSaved]);

  return (
    <Modal onHide={onDelete} closeOnClickOutside={false} allowToInterceptEscape fullscreen visible bare>
      <div className={rootClass}>
        <Modal.Header>
          <h1>{t("projects.create_project")}</h1>
          <ToggleItems items={steps} active={step} onSelect={setStep} />

          <Space>
            <Button
              variant="negative"
              look="outlined"
              onClick={onDelete}
              waiting={waiting}
              aria-label={t("pages.createproject.create_project.cancel_project_creation")}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={onCreate}
              waiting={waiting || uploading}
              waitingClickable={false}
              disabled={
                !project || uploadDisabled || !!error || !(name ?? "").trim() || dataMissingRequiredKeys || !!blocked
              }
              title={
                blocked
                  ? blocked
                  : dataMissingRequiredKeys
                    ? `Uploaded data is missing required ${
                        requiredKeysList.length === 1 ? "field" : "fields"
                      }: ${requiredKeysList
                        .filter((k) => !uploadedColumns.includes(k))
                        .map((k) => "$" + k)
                        .join(", ")}`
                    : undefined
              }
            >
              {t("common.save")}
            </Button>
          </Space>
        </Modal.Header>
        <ProjectName
          name={name}
          setName={setName}
          error={error}
          onSaveName={onSaveName}
          onSubmit={onCreate}
          description={description}
          plan={plan}
          setPlan={setPlan}
          setDescription={setDescription}
          workspaces={workspaces}
          workspaceId={workspaceId}
          setWorkspaceId={setWorkspaceId}
          panels={panels}
          fields={fieldsShown}
          show={step === "name"}
        />
        <ImportPage
          project={project}
          show={step === "import"}
          sample={sample}
          onSampleDatasetSelect={setSample}
          openLabelingConfig={() => setStep("config")}
          {...pageProps}
        />
        <ConfigPage
          project={project}
          config={draftConfig}
          onUpdate={(config) => {
            updateProject({ ...project, label_config: config });
          }}
          show={step === "config"}
          columns={columns}
          disableSaveButton={true}
        />
      </div>
    </Modal>
  );
};
